import { useEffect, useState } from 'react'
import { Menu, Checkbox, Stack } from '@mantine/core'
import { IconList } from '@tabler/icons-react'

// New design coral, overriding the app-wide blue Checkbox color set in the Mantine theme
// (components/LayoutContext.tsx). Instance styles win over the theme's component styles.
const CORAL_CHECKBOX_STYLES = {
  input: { '--checkbox-color': '#E5533D' },
} as const

type Props = {
  fields: { label: string; value: string }[]
  defaultChecked: string[]
  onChange: (fields: string[]) => void
  // Controlled open state so the parent can keep only one toolbar popup open at a time.
  opened?: boolean
  onOpenChange?: (opened: boolean) => void
}

export function FieldsMenu({ fields, defaultChecked, onChange, opened, onOpenChange }: Props) {
  const [checked, setChecked] = useState<string[]>([])

  useEffect(() => {
    setChecked(defaultChecked)
  }, [defaultChecked])

  const allChecked = checked.length === fields.length
  const isIndeterminate = checked.length > 0 && checked.length < fields.length

  const handleAllChange = () => {
    const newState = allChecked ? [] : fields.map((f) => f.value)
    setChecked(newState)
    onChange?.(newState)
  }

  const handleFieldChange = (value: string) => {
    setChecked((current) => {
      const newState = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]

      onChange?.(newState)
      return newState
    })
  }

  return (
    <Menu
      width={220}
      shadow="md"
      withArrow
      position="bottom-start"
      opened={opened}
      onChange={onOpenChange}
    >
      <Menu.Target>
        <button
          type="button"
          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-[#D8DBE0] bg-white px-2.5 py-[3px] !text-[16px] !font-semibold text-[#33383F] transition hover:bg-[#F4F6F8]"
        >
          <IconList size={16} />
          Fields
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <Stack gap={2} p={6}>
          <Checkbox
            label="All"
            checked={allChecked}
            indeterminate={isIndeterminate}
            onChange={handleAllChange}
            styles={CORAL_CHECKBOX_STYLES}
          />
          {fields.map((field) => (
            <Checkbox
              key={field.value}
              label={field.label}
              checked={checked.includes(field.value)}
              onChange={() => handleFieldChange(field.value)}
              styles={CORAL_CHECKBOX_STYLES}
            />
          ))}
        </Stack>
      </Menu.Dropdown>
    </Menu>
  )
}
